import { type BDMenuItem } from '@bizzdesign/shared/models';
import { type ProductAnalyticsService } from '@bizzdesign/shared/services';

const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

function toUnifyManagementUrl(configuredUrl: unknown): string | null {
  if (typeof configuredUrl !== 'string' || configuredUrl.trim() === '') {
    return null;
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(configuredUrl);
  } catch {
    return null;
  }

  const isLoopbackHttp =
    parsedUrl.protocol === 'http:' &&
    LOOPBACK_HOSTNAMES.has(parsedUrl.hostname);
  if (parsedUrl.protocol !== 'https:' && !isLoopbackHttp) {
    return null;
  }
  return parsedUrl.href;
}

/**
 * Builds the account menu item that opens Unify Management in a new tab.
 *
 * @param unifyManagementUrl - Base URL of the Unify Management app for the
 * current environment. Opening it relies on the shared Cognito session
 * between Unify and Unify Management, so no separate auth token is passed.
 * @param productAnalyticsService - Service used to record the
 * `unify_management_clicked` event when the item is activated.
 * @returns A `BDMenuItem` whose `command` opens `unifyManagementUrl` in a new
 * browser tab, or does nothing when the URL fails validation.
 */
export function createUnifyManagementMenuItem(
  unifyManagementUrl: string,
  productAnalyticsService: ProductAnalyticsService,
): BDMenuItem {
  // Validated here, right next to the window.open it guards, so the SAST
  // guard stays visible at the sink — don't move this check back out.
  const safeUrl = toUnifyManagementUrl(unifyManagementUrl);
  return {
    id: 'unify-management',
    labelKey: 'header.unifyManagement',
    tScope: 'featureLayout',
    icon: 'ui-settings',
    command: () => {
      if (safeUrl === null) {
        return;
      }
      productAnalyticsService.sendEvent('unify_management_clicked');
      window.open(safeUrl, '_blank', 'noopener,noreferrer');
    },
  };
}
